import { createSlice, Draft, PayloadAction } from "@reduxjs/toolkit";
import { ReactNode } from "react";
import { FilterType, isMultiValueFilter, TableFilterCommon, TextFilterMode } from "../types";
import { ComboboxOption } from "@altimateai/lego";

export interface FilterValue {
  type: FilterType;
  value: string | string[] | number | Date;
  operator?: "equals" | "contains" | "greater_than" | "less_than";
  options?: Array<{ label: string; value: string }>;
}

/**
 * Filter configuration stored in state for rendering in the filter sheet modal.
 * Extends TableFilterCommon with optional filter-type-specific rendering fields.
 */
export interface StoredFilterConfig extends TableFilterCommon {
  filterType?: FilterType;
  filterMode?: TextFilterMode;
  filterModeSearchKey?: string;
  options?: Array<ComboboxOption & { subComponent?: (close: () => void) => ReactNode }>;
  placeHolder?: string;
  allowFractional?: boolean;
  pageSize?: number;
  seeMore?: boolean;
  apiFunction?: (params: { prefix: string; page: number; size?: number }) => Promise<unknown>;
  extractorFunction?: (data: unknown) => Array<{ label: unknown; value: string }>;
}

export interface DatatableFilter {
  columnId: string;
  filterValue: FilterValue;
  label: string;
  /** Filter configuration for rendering in the filter sheet modal */
  filterConfig?: StoredFilterConfig;
}

export interface DatatableContextState {
  filters: Record<string, DatatableFilter>;
  totalPages: number;
  totalItems: number;
}

export const initialState: DatatableContextState = {
  filters: {},
  totalPages: 0,
  totalItems: 0,
};

const datatableContextSlice = createSlice({
  name: "datatableContext",
  initialState,
  reducers: {
    setFilters: (
      state,
      action: PayloadAction<
        Array<{ columnId: string; filterData: Omit<DatatableFilter, "columnId"> }>
      >
    ) => {
      action.payload.forEach(({ columnId, filterData }) => {
        // Preserve any value the URL-sync layer (or a prior register
        // call) already wrote into this slot. ``registerFilters`` is
        // commonly called more than once per page lifecycle — e.g. a
        // header hook re-runs when async filter options resolve and
        // the FilterConfig's ``options`` array changes — and without
        // this guard each re-register would overwrite a URL-applied
        // selection (``?userName=alice``) with the FilterConfig's
        // empty default, silently desyncing the More-Filters drawer
        // from the URL while the API stays correctly filtered. The
        // first-time register path is unchanged (no prior value, the
        // ``??`` falls through to ``filterData.filterValue.value``).
        const existingValue = state.filters[columnId]?.filterValue.value;
        // Type assertion needed because TableFilter contains function types that aren't compatible with immer's WritableDraft
        state.filters[columnId] = {
          ...filterData,
          columnId,
          filterValue: {
            ...filterData.filterValue,
            value: existingValue ?? filterData.filterValue.value,
          },
        } as Draft<DatatableFilter>;
      });
    },
    removeFilter: (state, action: PayloadAction<{ columnId: string }>) => {
      delete state.filters[action.payload.columnId];
    },
    clearAllFilters: state => {
      state.filters = {};
    },
    updateFilterValue: (
      state,
      action: PayloadAction<{ columnId: string; value: string | string[] | number }>
    ) => {
      const { columnId, value } = action.payload;
      if (state.filters[columnId]) {
        state.filters[columnId].filterValue.value = value;
      }
    },
    updateFilterValues: (
      state,
      action: PayloadAction<Array<{ columnId: string; value: string[] }>>
    ) => {
      action.payload.forEach(({ columnId, value }) => {
        if (state.filters[columnId]) {
          const filter = state.filters[columnId];
          const filterExpectsArray = isMultiValueFilter(filter.filterValue.type);

          // Handle array vs single value based on filter type configuration
          if (filterExpectsArray) {
            // Filter expects array - use value as-is
            state.filters[columnId].filterValue.value = value;
          } else {
            // Filter expects single value - take first element or empty string
            state.filters[columnId].filterValue.value = value.length > 0 ? value[0] : "";
          }
        }
      });
    },
    setTotalPages: (state, action: PayloadAction<number>) => {
      state.totalPages = action.payload;
    },
    setTotalItems: (state, action: PayloadAction<number>) => {
      state.totalItems = action.payload;
    },
  },
});

export const {
  setFilters,
  removeFilter,
  clearAllFilters,
  updateFilterValue,
  updateFilterValues,
  setTotalPages,
  setTotalItems,
} = datatableContextSlice.actions;

export default datatableContextSlice;
